import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@pozi/authmodule';
import { CookieService } from 'ngx-cookie-service';
import {
  BehaviorSubject,
  catchError,
  filter,
  MonoTypeOperatorFunction,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import {
  CompanyUser,
  ICompany,
  ICompanyUser,
  ISite,
} from '../models/company-user';
import { hasRouteParams } from '../shared/utils/route-utils';

@Injectable({
  providedIn: 'root',
})
export class CompanySiteService {
  private cookieService = inject(CookieService);
  private http = inject(HttpClient);
  private authService =
    inject<AuthService<CompanyUser, ICompanyUser>>(AuthService);
  private route = inject(ActivatedRoute);

  /** Name of the cookie used to store the currently selected company ID. */
  private readonly companyCookieName = 'X-Company';

  /** Name of the cookie used to store the currently selected site ID. */
  private readonly siteCookieName = 'X-Site';

  /**
   * Holds the list of companies available to the current user.
   * Emits updates whenever the user's companies change, typically after login.
   */
  private companies$ = new BehaviorSubject<ICompany[]>([]);

  /**
   * Holds the currently selected company.
   * Updates trigger corresponding updates to the selected site and API state.
   */
  private selectedCompany$ = new BehaviorSubject<ICompany | undefined>(
    undefined
  );

  /**
   * Holds the list of sites for the currently selected company.
   * Automatically updated when `selectedCompany$` changes.
   */
  private sites$ = new BehaviorSubject<ISite[]>([]);

  /**
   * Holds the currently selected site.
   * Updates trigger API synchronization and cookie updates via `changeCompanySite()`.
   */
  private selectedSite$ = new BehaviorSubject<ISite | undefined>(undefined);

  /**
   * Expiration date for the company and site cookies.
   * Initialized to one year from creation and used when saving `X-Company` and `X-Site`.
   */
  private companyAndSiteCookieExpirationDate = new Date();

  constructor() {
    this.companyAndSiteCookieExpirationDate.setFullYear(
      this.companyAndSiteCookieExpirationDate.getFullYear() + 1
    );

    // Subscribe to user changes to populate companies and the selected company
    this.authService.$user.subscribe((user) => {
      if (user && user.companies) {
        this.companies$.next(user.companies);
        const companyId = this.getCompanyIdCookie();
        const companyfromCookies = user.companies.find(
          (company) => company.id === companyId
        );
        this.selectedCompany$.next(companyfromCookies ?? user.companies[0]);
      }
    });

    // Update sites and selected site whenever the selected company changes
    this.selectedCompany$.subscribe((company) => {
      if (company) {
        this.sites$.next(company.sites);
        const siteId = this.getSiteIdCookie();
        const sitefromCookies = company.sites.find(
          (site) => site.id === siteId
        );
        const defaultSite = company.sites.find((site) => site.default);
        const site = sitefromCookies ?? defaultSite;
        this.selectedSite$.next(site ?? company.sites[0]);
      }
    });
  }

  setSelectedCompany(company: ICompany | undefined) {
    if (!company) return;
    this.selectedCompany$.next(company);
    this.changeCompanySite();
  }

  setSelectedSite(site: ISite | undefined) {
    if (!site) return;
    this.selectedSite$.next(site);
    this.changeCompanySite();
  }

  /**
   * Applies the currently selected company and site.
   *
   * Calls `persistCompanySite()` to update the back-end session and cookies.
   *
   * After a successful update, reloads the page to apply the new company/site context.
   *
   * If the update fails with a 401 (unauthorized),
   * selects a fallback company/site combination via `persistFallbackCompanySite()`.
   */
  changeCompanySite(): void {
    const company = this.selectedCompany$.value;
    const site = this.selectedSite$.value;
    if (!company || !site) return;

    this.persistCompanySite(company.id, site.id)
      .pipe(this.handleCompanySiteError())
      .subscribe(() => {
        if (hasRouteParams(this.route)) {
          window.location.replace('');
        } else {
          window.location.reload();
        }
      });
  }

  /**
   * Switches the active company and site to the given IDs, persisting the
   * selection to the back-end session and cookies, WITHOUT reloading the page.
   *
   * Intended for deep-linked flows (e.g. report routes) that must establish the
   * correct company/site context before any data is fetched, since back-end
   * responses vary by the currently selected company and site.
   *
   * Short-circuits when the cookies already match the requested IDs, so the
   * back-end is not contacted again for an already-active context.
   *
   * A company/site the account cannot switch to is answered with a 401, which
   * `handleCompanySiteError` turns into the fallback context rather than an
   * error — so a caller that must have the requested context has to confirm it
   * with {@link hasContext} once this completes.
   *
   * @param companyId - The ID of the company to switch to.
   * @param siteId - The ID of the site to switch to.
   * @returns An `Observable<void>` that completes once the context is applied.
   */
  switchToCompanySite(companyId: number, siteId: number): Observable<void> {
    if (this.hasContext(companyId, siteId)) {
      return of(undefined);
    }

    return this.persistCompanySite(companyId, siteId).pipe(
      tap(() => {
        const company = this.companies$.value.find((c) => c.id === companyId);
        if (company) {
          this.selectedCompany$.next(company);
          const site = company.sites.find((s) => s.id === siteId);
          this.selectedSite$.next(site ?? company.sites[0]);
        }
      }),
      this.handleCompanySiteError()
    );
  }

  /**
   * Sends a request to the back-end to persist the selected company and site, and updates cookies.
   *
   * @param companyId - The ID of the company to set as active.
   * @param siteId - The ID of the site to set as active.
   * @returns An `Observable<void>` that completes when the request succeeds and cookies are updated.
   */
  private persistCompanySite(
    companyId: number,
    siteId: number
  ): Observable<void> {
    const body = { company: companyId, site: siteId };

    return this.http.post<void>('auth/v1/change-company-site', body).pipe(
      tap(() => {
        this.saveCompanyIdCookie(companyId);
        this.saveSiteIdCookie(siteId);
      })
    );
  }

  /**
   * Validates the company and site cookies and ensures a valid selection is applied.
   *
   * - If valid cookies exist, applies them via `persistCompanySite()`.
   * - If cookies are missing, selects the first available company and site as a fallback and applies them.
   *
   * @returns An `Observable<void>` that completes after the company/site has been applied.
   */
  validateCompanySiteCookies(): Observable<void> {
    const companyIdCookie = this.getCompanyIdCookie();
    const siteIdCookie = this.getSiteIdCookie();

    if (companyIdCookie && siteIdCookie) {
      return this.persistCompanySite(companyIdCookie, siteIdCookie).pipe(
        this.handleCompanySiteError()
      );
    }

    return this.companies$.pipe(
      filter((companies) => companies.length > 0),
      take(1),
      switchMap((companies) => {
        const fallbackCompany = companies[0];
        const fallbackSite = fallbackCompany.sites[0];
        return this.persistCompanySite(fallbackCompany.id, fallbackSite.id);
      })
    );
  }

  private saveCompanyIdCookie(companyId: number): void {
    this.cookieService.set(this.companyCookieName, String(companyId), {
      expires: this.companyAndSiteCookieExpirationDate,
      path: '/',
      secure: true,
      sameSite: 'Lax',
    });
  }

  private saveSiteIdCookie(siteId: number): void {
    this.cookieService.set(this.siteCookieName, String(siteId), {
      expires: this.companyAndSiteCookieExpirationDate,
      path: '/',
      secure: true,
      sameSite: 'Lax',
    });
  }

  /**
   * Selects a fallback company and site when the current selection is invalid or unauthorized.
   * This function ensures that the application always has a valid company/site context.
   */
  private persistFallbackCompanySite(): Observable<void> {
    const companies = this.companies$.value;
    if (!companies?.length) {
      return throwError(() => new Error('No companies available'));
    }

    const fallbackCompany = companies[0];
    const fallbackSite = fallbackCompany.sites[0];

    this.selectedCompany$.next(fallbackCompany);
    this.selectedSite$.next(fallbackSite);

    return this.persistCompanySite(fallbackCompany.id, fallbackSite.id);
  }

  private handleCompanySiteError(): MonoTypeOperatorFunction<void> {
    return catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return this.persistFallbackCompanySite();
      }
      return throwError(() => error);
    });
  }

  /**
   * Whether the company/site context currently applied to the API session (the
   * `X-Company` / `X-Site` cookies, sent with every request) is exactly the
   * given pair.
   *
   * Deep-linked flows use it to tell a successful {@link switchToCompanySite}
   * from the silent 401 fallback to the account's first company/site.
   */
  hasContext(companyId: number, siteId: number): boolean {
    return (
      this.getCompanyIdCookie() === companyId &&
      this.getSiteIdCookie() === siteId
    );
  }

  private getCompanyIdCookie(): number {
    return parseInt(this.cookieService.get(this.companyCookieName));
  }

  private getSiteIdCookie(): number {
    return parseInt(this.cookieService.get(this.siteCookieName));
  }

  getCompanies(): Observable<ICompany[]> {
    return this.companies$.asObservable();
  }

  getSites(): Observable<ISite[]> {
    return this.sites$.asObservable();
  }

  getSelectedCompany(): Observable<ICompany | undefined> {
    return this.selectedCompany$.asObservable();
  }

  getSelectedSite(): Observable<ISite | undefined> {
    return this.selectedSite$.asObservable();
  }
}
